import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, downloadApiFile } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* The bracket: drawing it, and then playing it.

   ★★ This is the first screen on the platform where a competition is PLAYED rather than set up.
   Everything before it — the format, the numbers, the field, the game — describes a day; this one
   is the day happening. That is why it is a break-out and not another block on the cockpit: the
   cockpit answers "is my competition ready", and this answers "who is winning".

   ★★ The screen has two completely different faces and it must not pretend otherwise. Before the
   draw it is a list of the field with one button; after it, it is a tree of results. The hinge is
   `bracket.drawn`, and the "before" face carries the REASONS the button is greyed rather than a
   dead button — every refusal the API can give has a sentence here, so nobody has to press
   something to find out why it will not work.

   ★ Entering a result is a HOST power, drawing is the admin's. The two are separately gated in the
   API and separately shown here: a host running the day sees the result cards live and the
   "redraw" danger zone not at all.

   ★ Ordering the field is arrows, not drag-and-drop. This is used on a phone at a venue; a drag
   target is a fiddly thing to hit with a thumb and it has no keyboard story at all.

   ★ The round names come from the SAME translation keys the preview grid on the cockpit uses
   (`tournamentGridFinal` and friends). Two screens showing the same bracket must not call the
   semi-finals two different things.

   ★★ It serves EVERY format on the match engine, not only the bracket, and it asks the payload's
   `kind` rather than the format's name. A tree and a league table are the same screen underneath —
   rounds of fixtures with a result card on each — and they differ in exactly two places: what the
   finishing order looks like (a podium of three, or a table of everybody) and whether a fixture
   may end level. Both come off `FormatSpec.ranked_by`, which is the half of a format that says how
   it is RANKED. */

const RESULT_EMPTY = { winner: null, home_score: '', away_score: '' };

/* What a round is called. `from_end` counts back from the final, which is how brackets are named —
   "round 4 of 5" is not something an organiser was ever looking for.

   ★ NULL means the rounds have no names, and then "round 7" is the whole truth: a round robin's
   last round is round eleven and nothing more. Printing "the semi-finals" over it would describe a
   different competition. */
const roundName = (round, t) => {
  if (round.from_end === -1) return t('tournamentGridThird');
  if (round.from_end === 0) return t('tournamentGridFinal');
  if (round.from_end === 1) return t('tournamentGridSemi');
  if (round.from_end === 2) return t('tournamentGridQuarter');
  return t('tournamentGridRound', { n: round.round });
};

/* One side of a box: a competitor, or — when there is not one yet — where they will come from.

   ★ The second half is the more interesting one on a live bracket, so it is never left blank. It
   names the match it is waiting on ("winner of quarter-final 2") rather than the id, which is why
   `names` is threaded in: the payload gives every box its own name, and a side only has to look
   the one it points at up. */
const sideLabel = (side, t, names) => {
  if (side && side.competitor) return side.competitor.name || side.competitor.number || '—';
  if (side && side.from) {
    const of = (names || {})[side.from.match_id] || '';
    return side.from.outcome === 'loser'
      ? t('bracketLoserOfMatch', { match: of })
      : t('bracketWinnerOfMatch', { match: of });
  }
  /* ★ A pools day's final round waits on a TABLE, not on a match, and it said "Waiting" for all of
     it until the pools had been played. The same words the preview grid uses (`PlanGrid.bracketSide`),
     because the printed sheet, the preview and this screen must not name one box three ways. */
  if (side && side.from_pool) {
    return t(`tournamentGridPoolPlace_${side.from_pool.place > 2 ? 'n' : side.from_pool.place}`,
      { pool: side.from_pool.pool, place: side.from_pool.place });
  }
  return t('bracketWaiting');
};

/* {match id: what that box is called} — so a side can name the box it is waiting on. Numbered
   within its round when there is more than one, because "winner of the quarter-final" is ambiguous
   when there are four of them. */
const matchNames = (bracket, t) => {
  const out = {};
  (bracket.rounds || []).forEach((round) => {
    const many = round.matches.length > 1;
    round.matches.forEach((match, index) => {
      const base = roundName({ from_end: match.from_end, round: match.round }, t);
      out[match.id] = many ? `${base} ${index + 1}` : base;
    });
  });
  return out;
};

const CompanyEventBracket = () => {
  // ★ `i18n.language` rides along on the print links: the PDF is rendered server-side and looks its
  //   words up in this very locale file, so a Dutch organiser gets a Dutch sheet.
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(null);       // which match is being scored
  const [entry, setEntry] = useState(RESULT_EMPTY);
  const [order, setOrder] = useState(null);     // the field being reordered, before it is saved

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/bracket`)
      .then(setData)
      .catch((e) => setNotice(apiErrorText(t, e)));
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const call = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}/bracket${path}`, body)
      .then((res) => { setData(res); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const bracket = data.bracket || {};
  const drawn = !!bracket.drawn;
  const canManage = !!data.can_manage;
  const blocked = data.blocked || [];
  const seeded = (data.stage || {}).seeding === 'seeded';
  const field = order || data.field || [];
  const locked = busy;
  /* ★ A tree or a table — the format's own answer, never its name. Everything else on this screen
     is identical for both. */
  const isTable = data.kind === 'table';
  const word = (key) => t(isTable ? `fixtures${key}` : `bracket${key}`);

  const save = () => {
    call('PUT', '/seeding', { order: field.map((f) => f.id) })
      .then(() => setOrder(null))
      .catch(() => {});
  };

  const move = (index, by) => {
    const next = [...field];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  };

  const score = (match) => {
    if (!entry.winner) { setNotice(t('bracketPickAWinner')); return; }
    call('PUT', `/match/${match.id}`, entry)
      .then(() => { setOpen(null); setEntry(RESULT_EMPTY); })
      .catch(() => {});
  };

  /* ★ "Level" only where the formula says a match may end that way. It is a rule of the format
     resolved for THIS event, so it arrives in the payload rather than being guessed from the kind:
     a round robin with draws switched off must not offer a button the engine will refuse. */
  const sides = data.draws_allowed ? ['home', 'draw', 'away'] : ['home', 'away'];

  const names = matchNames(bracket, t);
  const standings = data.standings || {};
  const nameOf = (id) => {
    const found = (bracket.field || []).find((c) => c.id === id);
    return found ? (found.name || found.number) : null;
  };

  /* One box. Closed it is a line; open it is the result card. A decided box shows the winner in
     bold and nothing else — the score is on the card, not shouted from the list. */
  const matchRow = (match) => {
    // ⚠️ Played, not "has a winner": a draw is a played fixture with nobody winning it, and
    //    testing the winner would leave it looking like a match that had not happened.
    const played = ['done', 'walkover', 'drawn'].includes(match.status);
    const decided = !!match.winner_id;
    const homeWon = decided && match.home && match.home.competitor
      && match.home.competitor.id === match.winner_id;
    const isOpen = open === match.id;
    return (
      <li key={match.id} className={`kb-match${played ? ' kb-match--done' : ''}`}>
        <button type="button" className="kb-match__head"
                disabled={match.status === 'pending'}
                onClick={() => {
                  setOpen(isOpen ? null : match.id);
                  setEntry(RESULT_EMPTY);
                }}>
          <span className="kb-match__when">
            {match.start ? match.start.slice(11) : '—'}
            {match.court ? <span className="kb-match__court">
              {t('bracketCourt', { n: match.court })}</span> : null}
          </span>
          <span className={`kb-match__side${homeWon ? ' kb-match__side--won' : ''}`}>
            {sideLabel(match.home, t, names)}
          </span>
          <span className="kb-match__v">{t('tournamentGridVersus')}</span>
          <span className={`kb-match__side${decided && !homeWon ? ' kb-match__side--won' : ''}`}>
            {sideLabel(match.away, t, names)}
          </span>
          {played && (match.home_score !== null || match.away_score !== null) && (
            <span className="kb-match__score">{match.home_score} – {match.away_score}</span>
          )}
        </button>

        {isOpen && (
          <div className="kb-card">
            {played ? (
              <>
                <p className="ct-field__hint">
                  {decided
                    ? t('bracketAlreadyDecided', { name: nameOf(match.winner_id) || '' })
                    : t('fixturesAlreadyLevel')}
                </p>
                <button type="button" className="btn btn-outline-secondary btn-sm"
                        disabled={locked}
                        onClick={() => call('DELETE', `/match/${match.id}`)
                          .then(() => setOpen(null)).catch(() => {})}>
                  {t('bracketUndoResult')}
                </button>
              </>
            ) : (
              <>
                {/* ★ "Who won" first and always, the scores second and optional. A game judged on
                    time or by a jury must still be a game a bracket can be played over. */}
                <p className="ct-pick__label">{t('bracketWhoWon')}</p>
                <div className="kb-pick">
                  {sides.map((which) => (
                    <button key={which} type="button"
                            className={`kb-pick__one${entry.winner === which
                              ? ' kb-pick__one--on' : ''}`}
                            onClick={() => setEntry({ ...entry, winner: which })}>
                      {which === 'draw'
                        ? t('fixturesLevel')
                        : sideLabel(match[which], t, names)}
                    </button>
                  ))}
                </div>
                <div className="kb-scores">
                  <span className="ct-field">
                    <label htmlFor={`h-${match.id}`}>{sideLabel(match.home, t, names)}</label>
                    <input id={`h-${match.id}`} type="number" step="any" value={entry.home_score}
                           onChange={(e) => setEntry({ ...entry, home_score: e.target.value })} />
                  </span>
                  <span className="ct-field">
                    <label htmlFor={`a-${match.id}`}>{sideLabel(match.away, t, names)}</label>
                    <input id={`a-${match.id}`} type="number" step="any" value={entry.away_score}
                           onChange={(e) => setEntry({ ...entry, away_score: e.target.value })} />
                  </span>
                </div>
                <p className="ct-field__hint">{t('bracketScoresOptional')}</p>
                <button type="button" className="btn btn-primary btn-sm" disabled={locked}
                        onClick={() => score(match)}>
                  {t('bracketConfirm')}
                </button>
                <label className="ce-check ms-3">
                  <input type="checkbox" checked={!!entry.walkover}
                         onChange={(e) => setEntry({ ...entry, walkover: e.target.checked })} />
                  {t('bracketWalkover')}
                </label>
              </>
            )}
          </div>
        )}
      </li>
    );
  };

  /* The table: everybody, in order, with the sums that produced that order.
     ★★ It prints what it COUNTED and not only the total. "14 points" is a number nobody can check;
     played/won/drawn/lost beside it is the same number somebody can add up themselves — the same
     rule the ranking screen follows when it opens a team's heats. */
  const tableBlock = (one) => (
    <div key={one.pool || 'all'} className="kb-round">
      {one.pool && <p className="ct-plan__roundname">{t('fixturesPool', { pool: one.pool })}</p>}
      {/* ★ The same table shell the preview grid and the ladder use — it is the same kind of thing,
          a small square of numbers read across, and it scrolls sideways on its own rather than
          pushing the page wide. A third table style for a third table is how a screen stops looking
          like one screen. */}
      <div className="ct-plan__scroll">
        <table className="ct-plan__table kb-table">
          <thead>
            <tr>
              <th>{t('fixturesTableRank')}</th>
              <th>{t('fixturesTableWho')}</th>
              <th>{t('fixturesTablePlayed')}</th>
              <th>{t('fixturesTableWon')}</th>
              <th>{t('fixturesTableDrawn')}</th>
              <th>{t('fixturesTableLost')}</th>
              <th>{t('fixturesTableFor')}</th>
              <th>{t('fixturesTableAgainst')}</th>
              <th>{t('fixturesTablePoints')}</th>
            </tr>
          </thead>
          <tbody>
            {one.rows.map((row) => (
              <tr key={row.competitor_id}>
                {/* ★ "=1" where two are level and nothing separated them. Inventing an order the
                    competition never produced is the one thing a table must not do. */}
                <td>{row.shared ? `=${row.rank}` : row.rank}</td>
                <td>{nameOf(row.competitor_id) || '—'}</td>
                <td>{row.played}</td>
                <td>{row.won}</td>
                <td>{row.drawn}</td>
                <td>{row.lost}</td>
                <td>{row.scored}</td>
                <td>{row.conceded}</td>
                <td><strong>{row.points}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="trophy" theme="primary" className="me-2" />
          {word('Title')}
        </Title>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {!drawn && (
          <>
            <p className="bravos-q__hint mb-2">{word('Intro')}</p>

            {blocked.length > 0 && (
              <div className="ce-note ce-note--warn">
                <p className="mb-1">{word('NotYet')}</p>
                <ul className="mb-0">
                  {blocked.map((code) => <li key={code}>{t(code)}</li>)}
                </ul>
              </div>
            )}

            {/* ★ The field, in the order the draw will take it. Shown even for a random draw —
                it is the list of who is in, which is the thing an organiser checks last. */}
            <p className="ct-pick__label mt-3">{t('bracketField', { n: field.length })}</p>
            {seeded
              ? <p className="ct-field__hint">{t('bracketOrderHintSeeded')}</p>
              : <p className="ct-field__hint">{t('bracketOrderHintRandom')}</p>}
            {data.seed_note && (
              <p className="ce-note">
                {t('bracketSeedNote', {
                  added: data.seed_note.added, dropped: data.seed_note.dropped,
                })}
              </p>
            )}
            <ol className="kb-field">
              {field.map((team, index) => (
                <li key={team.id}>
                  <span className="kb-field__n">{index + 1}</span>
                  <span className="kb-field__name">{team.name}</span>
                  {canManage && seeded && (
                    <span className="kb-field__move">
                      <button type="button" disabled={locked || index === 0}
                              aria-label={t('bracketMoveUp')}
                              onClick={() => move(index, -1)}>↑</button>
                      <button type="button" disabled={locked || index === field.length - 1}
                              aria-label={t('bracketMoveDown')}
                              onClick={() => move(index, 1)}>↓</button>
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {order && (
              <button type="button" className="btn btn-outline-primary btn-sm mb-3"
                      disabled={locked} onClick={save}>
                {t('bracketSaveOrder')}
              </button>
            )}

            {canManage && (
              <div className="mt-3">
                <button type="button" className="btn btn-primary"
                        disabled={locked || blocked.length > 0}
                        onClick={() => call('POST', '', {}).catch(() => {})}>
                  <Icon name="shuffle" className="me-1" />{word('Draw')}
                </button>
                <p className="ct-field__hint mt-2">{word('DrawHint')}</p>
              </div>
            )}
          </>
        )}

        {drawn && (
          <>
            {/* ★★ Paper. A bracket is run on the court, and until now none of this could leave the
                screen. TWO sheets on purpose and not one with a switch: the blank one goes out with
                whoever is refereeing and comes back written on, while the other is the state of the
                competition at the moment it was printed. They are used at different moments by
                different people, so they are two buttons. */}
            <div className="kb-print mb-3">
              <button type="button" className="btn btn-outline-primary btn-sm me-2"
                      onClick={() => downloadApiFile(`/company/event/${eventId}/print/bracket.pdf`,
                        { lang: i18n.language, blank: 1 })}>
                <Icon name="print" className="me-1" />{t('printBracketBlank')}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm"
                      onClick={() => downloadApiFile(`/company/event/${eventId}/print/bracket.pdf`,
                        { lang: i18n.language })}>
                <Icon name="file-download" className="me-1" />{t('printBracketLive')}
              </button>
            </div>

            {/* The finishing order, once there is one. Champion, runner-up, third — and nothing
                below, because a bracket does not rank the rest and inventing a table would be a
                ranking nobody played for. */}
            {standings.champion && (
              <div className="kb-podium">
                <p className="kb-podium__one kb-podium__one--first">
                  🥇 {nameOf(standings.champion)}
                </p>
                {standings.runner_up && (
                  <p className="kb-podium__one">🥈 {nameOf(standings.runner_up)}</p>
                )}
                {standings.third && (
                  <p className="kb-podium__one">🥉 {nameOf(standings.third)}</p>
                )}
              </div>
            )}

            {/* ★★ The table, where that is what this format is ranked on. One for a round robin;
                one per pool for a pools day — same component, and the pool falls out of who is
                entered where. Above the fixtures, because it is the thing an organiser came to
                look at. */}
            {isTable && (data.standing || {}).tables && (
              <>
                <p className="ct-pick__label mt-2">{t('fixturesTable')}</p>
                {(data.standing.tables || []).map(tableBlock)}
                <p className="ct-field__hint">
                  {t('fixturesTableRules', {
                    win: (data.standing.rules || {}).points_win,
                    draw: (data.standing.rules || {}).points_draw,
                    tiebreak: t(`fixturesTiebreak_${(data.standing.rules || {}).tiebreak}`),
                  })}
                </p>
              </>
            )}

            {(bracket.byes || []).length > 0 && (
              <p className="ct-plan__byes">
                {t('tournamentGridByes', {
                  count: bracket.byes.length,
                  teams: bracket.byes.map((b) => b.name || b.number).join(', '),
                })}
              </p>
            )}

            {(bracket.rounds || []).map((round) => (
              <div key={round.round} className="kb-round">
                <p className="ct-plan__roundname">{roundName(round, t)}</p>
                {/* ⚠️ Per ROUND, not once at the top. In a bracket a bye is a fact about the draw
                    ("these twelve are through"); here a different competitor sits out every round
                    of an odd field, and reporting it the bracket's way would say they did not
                    play at all. */}
                {(round.byes || []).length > 0 && (
                  <p className="ct-plan__byes">
                    {t('fixturesSittingOut', {
                      teams: round.byes.map((b) => b.name || b.number).join(', '),
                    })}
                  </p>
                )}
                <ul className="kb-matches">{round.matches.map(matchRow)}</ul>
              </div>
            ))}

            {canManage && (
              <div className="kb-danger">
                <p className="ct-field__hint">{word('RedrawHint')}</p>
                <button type="button" className="btn btn-outline-danger btn-sm"
                        disabled={locked || data.has_result}
                        onClick={() => call('DELETE', '').catch(() => {})}>
                  {word('UndoDraw')}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyEventBracket;
